import { Injectable, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { User } from '../../../models/user.model';
import {
  OrderEthernet,
  OrderInternet,
  OrderMpls,
  OrderP2P,
  OrderSdWan,
  OrderType,
  OrderVoice,
  Order as OrdersTable,
  Status,
  getEnumOrderTypeName,
  getEnumStatusName,
} from '../../../orders-table/models';
import { types_dictionary } from '../../../orders-table/models/_order-types.dictionary';
import { transport_types } from '../../../orders-table/models/_transport-types.dictionary';
import OrderContactInfo from '../../../orders-table/order-contacts-box/order-contact-info.interface';
import { LoadOrdersV2 } from '../../../store/actions';
import { State } from '../../../store/reducers';
import { getActiveOrdersOrderTable } from '../../../store/selectors';
import {
  ElementWithRecordType,
  EnumStatusOption,
  EnumTypeOption,
  LineItemRecord,
  ParsedAddress,
  ParsedContact,
  SummaryResult,
} from './orders-parser.types';

@Injectable({
  providedIn: 'root',
})
export class OrdersParserService {
  private readonly store = inject(Store<State>);

  private cachedOrders = signal<OrdersTable[] | null>(null);

  clear(): void {
    this.cachedOrders.set(null);
  }

  getEnumTypes(): EnumTypeOption[] {
    return Object.values(OrderType)
      .filter((item) => !Number.isNaN(Number(item)))
      .map((type: OrderType) => ({
        value: type,
        label: getEnumOrderTypeName(type),
      }));
  }

  getEnumStatus(): EnumStatusOption[] {
    return Object.values(Status)
      .filter((item) => !Number.isNaN(Number(item)))
      .map((st: Status) => ({
        value: st,
        label: getEnumStatusName(st),
        child: !!(
          st === Status.FOC_PENDING_WAITING_VENDOR ||
          st === Status.FOC_PENDING_SPECIAL_CONSTRUCTION ||
          st === Status.FOC_PENDING_CARRIER_BUILD ||
          st === Status.FOC_PENDING_CUST_REQ
        ),
      }));
  }

  private validOrders(jsonData: LineItemRecord[]): LineItemRecord[] {
    const records = jsonData.map((row: LineItemRecord): LineItemRecord => {
      const dealStatus = row.Service__r?.Status__c;
      const record: LineItemRecord = { ...row };
      record.deal = row.deal ?? '';
      record.deal_status = dealStatus;
      record.contact_sales = this.parseContact(
        row.embeddedOrder?.Sales_Rep__r,
        'Sales Engineer',
      );
      record.contact_manager = this.parseContact(
        row.embeddedOrder?.Account_Manager__r,
      );
      if (
        row?.embeddedOrder?.Default_Task_Assignments__r?.records?.length > 0
      ) {
        const assignments =
          row.embeddedOrder.Default_Task_Assignments__r.records;
        record.other_contacts = assignments
          .map((rec) => this.parseContact(rec.User__r))
          .filter((contact): contact is ParsedContact => contact !== null);
      }
      record.order_date =
        (row.embeddedOrder?.Order_Date__c ?? null) !== null
          ? String(row.embeddedOrder.Order_Date__c)
          : undefined;
      record.id = row.embeddedOrder?.Id;
      return record;
    });
    return records.filter((el): el is LineItemRecord => el !== null);
  }

  private calculateStatus(record: LineItemRecord, _order: OrdersTable): Status {
    switch (record.Service_Order2__r?.Status__c) {
      case 'In Progress':
        return Status.IN_PROGRESS;
      case 'Ready':
      case 'Pending':
        return Status.PENDING;
      case 'Complete':
        return Status.COMPLETE;
      case 'Canceled':
        return Status.CANCELED;
      default:
        return Status.PENDING;
    }
  }

  private calculateStatusOrderTable(
    record: LineItemRecord,
    order: OrdersTable,
  ): Status {
    const lineType = record.Line_Item_Type__c;
    const sord = record.Service_Order2__r;
    const tasksArray = sord?.tasks;
    const [tasks = null] = tasksArray ?? [];
    const { type } = order;

    if (sord?.Status__c === 'Canceled') {
      return Status.CANCELED;
    }

    if (lineType === 'Disconnect') {
      return sord?.Status__c !== 'Complete'
        ? Status.DISCONNECTED_IN_PROGRESS
        : Status.DISCONNECTED;
    }

    if (lineType === 'Move/New') {
      return sord?.Status__c !== 'Complete'
        ? Status.MOVE_REQUESTED_IN_PROGRESS
        : Status.MOVE_REQUESTED;
    }

    if (sord?.Status__c === 'Canceled') {
      return Status.CANCELED;
    }

    if (lineType === 'Disconnect') {
      return sord?.Status__c !== 'Complete'
        ? Status.IN_PROGRESS
        : Status.COMPLETE;
    }

    if (lineType === 'Move/New') {
      return sord?.Status__c !== 'Complete'
        ? Status.IN_PROGRESS
        : Status.COMPLETE;
    }

    if (type === OrderType.SDWAN || type === OrderType.VOICE) {
      return sord?.Status__c !== 'Complete'
        ? Status.IN_PROGRESS
        : Status.COMPLETE;
    }

    // let's check the status of the circuit:
    let circuitStatus: Status = Status.IN_PROGRESS;
    if (tasks?.length > 0) {
      // check Submit Vendor Task:
      const taskSubmitVendor = tasks.filter(
        (t) => t.Name === 'Submit Vendor Order',
      );

      if (taskSubmitVendor.length > 0) {
        const [firstTask] = taskSubmitVendor;
        if (firstTask.Status__c === 'Pending') {
          circuitStatus = Status.PREPARING;
        }

        if (firstTask.Status__c === 'Complete') {
          circuitStatus = Status.FOC_PENDING;
        }
      }
    }

    if (sord?.Vendor_FOC_Date__c != null) {
      circuitStatus = Status.FOC_ASSIGNED;
    }

    // checking elements
    if (order.vendor_start_service != null) {
      circuitStatus = Status.VENDOR_COMPLETION;
    }

    if (sord?.Equipment_Shipment_Tracking_Number__c != null) {
      circuitStatus = Status.EQUIPMENT_SHIPPED;
    }

    if (tasks?.length > 0) {
      const taskScheduleActivation = tasks.filter(
        (t) => t.Name === 'Schedule Activation',
      );

      const taskActivateService = tasks.filter(
        (t) => t.Name === 'Activate Service',
      );

      if (taskActivateService.length > 0) {
        const [firstActivateTask] = taskActivateService;
        if (firstActivateTask.Status__c === 'Complete') {
          return Status.ACTIVATION_COMPLETE;
        }
      }
      if (taskScheduleActivation.length > 0) {
        const [firstScheduleTask] = taskScheduleActivation;
        const scheduleStatus = firstScheduleTask.Status__c;
        if (scheduleStatus === 'Ready' || scheduleStatus === 'In Progress') {
          return Status.ACTIVATION_PENDING;
        }

        if (scheduleStatus === 'Complete') {
          return Status.ACTIVATION_SCHEDULED;
        }
      }
    }

    return circuitStatus;
  }

  private calculatePendingReasons(record: LineItemRecord): Status[] {
    const tasksArray = record.Service_Order2__r?.tasks ?? [];
    if (tasksArray.length === 0) return [];

    const [tasks] = tasksArray ?? [];
    if (tasks.length === 0) return [];

    const reasons: Status[] = [];
    const construction = tasks.filter(
      (t) =>
        t.Name?.toLowerCase().includes('special construction') &&
        t.Status__c === 'In Progress',
    );

    if (construction.length > 0) {
      reasons.push(Status.FOC_PENDING_SPECIAL_CONSTRUCTION);
    }

    const reqs = tasks.filter(
      (t) =>
        t.Name?.toLowerCase().includes('customer site requirements') &&
        t.Status__c === 'In Progress',
    );

    if (reqs.length > 0) {
      reasons.push(Status.FOC_PENDING_CUST_REQ);
    }

    const vendor = tasks.filter(
      (t) =>
        t.Name?.toLowerCase().includes('vendor construction') &&
        t.Status__c === 'In Progress',
    );

    if (vendor.length > 0) {
      reasons.push(Status.FOC_PENDING_CARRIER_BUILD);
    }

    if (reasons.length === 0) {
      reasons.push(Status.FOC_PENDING_WAITING_VENDOR);
    }

    return reasons;
  }

  private parseAddress(record: LineItemRecord): ParsedAddress {
    const addr = record.Location__r;
    return {
      site: addr?.Name ?? '-',
      state: addr?.State__c ?? '-',
      city: addr?.City__c ?? '-',
      street: addr?.Street_1__c ?? '-',
      zip: addr?.Zip_Code__c ?? '-',
    };
  }

  private parseContact(
    contact?: User,
    position?: string,
  ): ParsedContact | null {
    if (!contact?.Id) {
      return null;
    }
    if (!position) {
      position = contact.Title || 'Another contact';
    }
    return {
      position,
      name: contact.Name ?? '',
      email: contact.Email ?? '',
      phone1: contact.Phone ?? '',
    };
  }

  private getStandardInterval(transportType: string): string {
    const [interval] = transport_types.filter(
      (t) => t.product === transportType.toLowerCase(),
    );
    return interval?.interval ?? '-';
  }

  private mapBasicService(
    order: OrdersTable,
    record: LineItemRecord,
  ): OrdersTable {
    const serviceOrder = record.Service_Order2__r;
    order.ready_date = serviceOrder?.CRDD_c;
    const service = record.Service__r;
    if (!service?.Id) return order;

    order.customer_handoff = service.Connector_Type_Z_Location__c;
    order.transport_type = service.Transport_Type__r?.Name;
    if (order.transport_type) {
      order.standard_interval = this.getStandardInterval(order.transport_type);
    }

    return order;
  }

  private mapCircuitInfo(
    order: OrdersTable,
    record: LineItemRecord,
  ): OrdersTable {
    this.mapBasicService(order, record);

    const service = record.Service__r;
    if (!service?.Id) return order;

    order.bandwith_down = service.Bandwidth_Downstream__c;
    order.bandwith_up = service.Bandwidth_Upstream__c;
    order.circuit_bandwith = `${order.bandwith_down}/${order.bandwith_up}`;
    order.booked_mrm = service.Booked_MRM__c
      ? String(service.Booked_MRM__c)
      : undefined;

    const elements = service.Elements__r;
    if (!elements.length) {
      return order;
    }

    const elementPort = (elements as ElementWithRecordType[]).find(
      (el) =>
        el.RecordType?.Name?.includes('Loop') ||
        el.RecordType?.Name?.includes('Port'),
    );
    const elementAccess = (elements as ElementWithRecordType[]).find(
      (el) =>
        el.RecordType?.Name?.includes('Loop') ||
        el.RecordType?.Name?.includes('Access'),
    );
    if (order.type === OrderType.ETHERNET) {
      order.vendor_name = elementAccess?.Vendor_Name__c;
      order.vendor_circuit_id = elementAccess?.Vendor_Circuit_Id__c;
    } else {
      order.vendor_name = elementPort?.Vendor_Name__c;
      order.vendor_circuit_id =
        elementPort?.Vendor_Circuit_Id__c ||
        elementAccess?.Vendor_Circuit_Id__c;
    }
    order.last_mile_circuit_id =
      elementAccess?.Last_Mile_Provider_Circuit_Id_Z_Location__c;
    order.circuit_demarc =
      elementPort?.Demarc_Z_Loc__c || elementAccess?.Demarc_Z_Loc__c;
    order.circuit_handoff = elementAccess?.Interface_Type_Z_Loc__c;
    order.enni =
      elementPort?.ENNI_UNI_Circuit_ID__c ||
      elementAccess?.ENNI_UNI_Circuit_ID__c;
    order.vendor_start_service =
      elementAccess?.Vendor_Start_Of_Service__c != null
        ? String(elementAccess.Vendor_Start_Of_Service__c)
        : undefined;

    if (order.type === OrderType.MPLS) {
      const privateElement = (elements as ElementWithRecordType[]).find(
        (el) => el.RecordType?.Name === 'Private Line',
      );
      order.vendor_name = privateElement?.Vendor_Name__c;
    }

    const elementWan = (elements as ElementWithRecordType[]).find((el) => {
      if (el.IP_Block_Address_Type__c !== 'WAN') return false;
      return (
        el.RecordType?.Name === 'IPv4 Block' ||
        el.RecordType?.Name === 'IPv6 Block'
      );
    });

    if (elementWan) {
      order.wan_network_address = elementWan.Network_Address_Text__c;
      order.wan_network_mask =
        elementWan.IPv4_Network_Mask_Text__c ||
        elementWan.IPv6_Network_Mask_Text__c;
      order.wan_gateway_ip = elementWan.IP_Block_Carrier_Address_Text__c;
    }
    const elementLan = (elements as ElementWithRecordType[]).find((el) => {
      if (el.IP_Block_Address_Type__c !== 'LAN') return false;
      return (
        el.RecordType?.Name === 'IPv4 Block' ||
        el.RecordType?.Name === 'IPv6 Block'
      );
    });
    if (elementLan?.Id) {
      order.lan_network_address = elementLan.Network_Address_Text__c;
      order.lan_network_mask =
        elementLan.IPv4_Network_Mask_Text__c ||
        elementLan.IPv6_Network_Mask_Text__c;
    }
    const equipment = (elements as ElementWithRecordType[]).find(
      (el) => el.RecordType?.Name === 'Equipment',
    );
    if (equipment?.Id) {
      order.equipment = equipment.Equipment_Supplemental_Product__r?.Name;
    }

    return order;
  }

  private mapP2P(order: OrderP2P, record: LineItemRecord): OrderP2P {
    this.mapCircuitInfo(order, record);
    const service = record.Service__r;
    let element: ElementWithRecordType | undefined;
    if (!service?.Id) return order;

    const elements = service.Elements__r;
    if (elements?.length > 0) {
      [element] = elements as ElementWithRecordType[];
      if (!element?.Id) return order;
    }

    order.location_a_address = element?.A_Location__r?.One_Line_Address__c;
    order.location_a_vendor_provider = element?.Last_Mile_Provider_A_Loc__c;
    order.location_a_circuit_id = element?.Vendor_Circuit_Id__c;
    order.location_a_foc_date = element?.Vendor_FOC_Date__c;
    order.location_z_address = element?.Service_Z_Location_Address__c;
    order.location_z_vendor_provider = element?.Last_Mile_Provider_Z_Loc__c;
    order.location_z_circuit_id =
      element?.Last_Mile_Provider_Circuit_Id_Z_Location__c;
    order.location_z_foc_date = element?.Vendor_FOC_Date__c;
    order.location_a_last_mile_id =
      element?.A_Location_Last_Mile_Provider_Circuit_Id__c;
    order.location_z_last_mile_id =
      element?.Last_Mile_Provider_Circuit_Id_Z_Location__c;
    order.location_a_demarc = element?.Demarc_A_Loc__c;
    order.location_z_demarc = element?.Demarc_Z_Loc__c;

    return order;
  }

  private mapEquipmentSDWan(
    order: OrderSdWan,
    record: LineItemRecord,
  ): OrderSdWan {
    this.mapBasicService(order, record);
    const service = record.Service__r ?? null;
    if (service === null) return order;
    const elements = service.Elements__r ?? null;

    if (elements === null) {
      return order;
    }

    const equipmentRecord = (elements as ElementWithRecordType[]).find(
      (el) => el.RecordType?.Name === 'Equipment',
    );
    if (equipmentRecord !== undefined) {
      order.equipment = equipmentRecord.Equipment_Supplemental_Product__r?.Name;
    }

    return order;
  }

  private mapType(record: LineItemRecord): OrdersTable | null {
    const type = record.Service_Type__c;
    if (!type) return null;

    if (types_dictionary.internet.includes(type)) {
      return this.mapCircuitInfo(new OrderInternet(), record);
    }
    if (types_dictionary.ethernet.includes(type)) {
      return this.mapCircuitInfo(new OrderEthernet(), record);
    }
    if (types_dictionary.mpls.includes(type)) {
      return this.mapCircuitInfo(new OrderMpls(), record);
    }
    if (types_dictionary.sdwan.includes(type)) {
      return this.mapEquipmentSDWan(new OrderSdWan(), record);
    }
    if (types_dictionary.voice.includes(type)) {
      return new OrderVoice();
    }
    if (types_dictionary.p2p.includes(type)) {
      return this.mapP2P(new OrderP2P(), record);
    }
    return null;
  }

  private mapOrder(
    record: LineItemRecord,
    isFromOrdersTable: boolean,
  ): OrdersTable | null {
    const o = this.mapType(record) ?? null;
    if (o === null) return null;

    const address = this.parseAddress(record);
    o.baseData({
      id: record.Id ?? '',
      nit: record.Service__r?.Name,
      site: address.site,
      state: address.state,
      city: address.city,
      address: address.street,
      zip: address.zip,
    });

    o.service_name = record.Service_Type__c;
    o.line_type = record.Line_Item_Type__c;
    o.local_contact = record.LocalContact;
    o.product = record.Service_Type__c;
    o.order_date = record.order_date;
    o.deal = record.deal;
    o.oli = record.Name;

    o.start_service =
      (record.Service__r?.Start_Of_Service__c ?? null) !== null
        ? String(record.Service__r.Start_Of_Service__c)
        : undefined;
    o.end_service =
      (record.Service__r?.End_Of_Service__c ?? null) !== null
        ? String(record.Service__r.End_Of_Service__c)
        : undefined;
    o.activation_date =
      (record.Service__r?.Activation_Date__c ?? null) !== null
        ? String(record.Service__r.Activation_Date__c)
        : undefined;

    o.contact_sales = record.contact_sales as OrderContactInfo | null;
    o.contact_manager = record.contact_manager as OrderContactInfo | null;
    if ((record.other_contacts ?? null) !== null) {
      o.other_contacts = record.other_contacts as OrderContactInfo[];
    }

    if (isFromOrdersTable) {
      o.milestone = this.calculateStatusOrderTable(record, o);
    } else {
      o.milestone = this.calculateStatus(record, o);
    }
    if (o.milestone === Status.FOC_PENDING) {
      o.foc_pending_reasons = this.calculatePendingReasons(record);
    }

    const serviceOrder = record.Service_Order2__r;
    if (serviceOrder != null) {
      o.tracking_number = serviceOrder.Equipment_Shipment_Tracking_Number__c;
      o.shipping_address = serviceOrder.Shipping_Address__c;
      // check if cancelled:
      o.foc_date =
        (serviceOrder.Vendor_FOC_Date__c ?? null) !== null
          ? String(serviceOrder.Vendor_FOC_Date__c)
          : undefined;
      if (serviceOrder.Status__c === 'Canceled') {
        o.canceled = true;
        o.cancel_date =
          (serviceOrder.Completed_Date__c ?? null) !== null
            ? String(serviceOrder.Completed_Date__c)
            : undefined;
      }
    }

    return o;
  }

  public getOrders(
    isFromOrdersTable: boolean,
  ): Observable<OrdersTable[] | null> {
    if (this.cachedOrders() !== null) {
      return new Observable((observer) => observer.next(this.cachedOrders()));
    }

    this.store.dispatch(new LoadOrdersV2());
    return this.store.select(getActiveOrdersOrderTable).pipe(
      map((data) => {
        this.cachedOrders.set(
          this.validOrders(data as unknown as LineItemRecord[])
            .map((record) => this.mapOrder(record, isFromOrdersTable))
            .filter((o): o is OrdersTable => o !== null),
        );
        return this.cachedOrders();
      }),
    );
  }

  public getSummary(): Observable<SummaryResult | null> {
    const types = {
      all: 0,
    };
    const activeTypes: Record<string, number> = {};
    const status = {
      in_progress: 0,

      preparing: 0,
      pending_foc: 0,
      pending_foc_waiting: 0,
      pending_foc_construction: 0,
      pending_foc_carrier: 0,
      pending_foc_customer: 0,
      foc_assigned: 0,
      vendor_completion: 0,
      equipment_shipped: 0,
      pending_activation: 0,
      scheduled_activation: 0,
      complete: 0,

      canceled: 0,
      disconnected: 0,
      disconnected_ip: 0,
      move_request: 0,
      activation_complete: 0,
      move_request_ip: 0,
    };
    const pendingReasons = {
      waiting_vendor: 0,
      special_construction: 0,
      carrier_build: 0,
      cust_req: 0,
    };

    const statusParse = (order: OrdersTable) => {
      switch (order.milestone) {
        case Status.PREPARING:
          status.preparing += 1;
          status.in_progress += 1;
          break;
        case Status.FOC_PENDING:
          status.pending_foc += 1;
          status.in_progress += 1;
          order.foc_pending_reasons.forEach((o) => {
            if (o === Status.FOC_PENDING_WAITING_VENDOR) {
              pendingReasons.waiting_vendor += 1;
            } else if (o === Status.FOC_PENDING_SPECIAL_CONSTRUCTION) {
              pendingReasons.special_construction += 1;
            } else if (o === Status.FOC_PENDING_CARRIER_BUILD) {
              pendingReasons.carrier_build += 1;
            } else if (o === Status.FOC_PENDING_CUST_REQ) {
              pendingReasons.cust_req += 1;
            }
          });
          break;
        case Status.FOC_ASSIGNED:
          status.foc_assigned += 1;
          status.in_progress += 1;
          break;
        case Status.VENDOR_COMPLETION:
          status.vendor_completion += 1;
          status.in_progress += 1;
          break;
        case Status.EQUIPMENT_SHIPPED:
          status.equipment_shipped += 1;
          status.in_progress += 1;
          break;
        case Status.ACTIVATION_PENDING:
          status.pending_activation += 1;
          status.in_progress += 1;
          break;
        case Status.ACTIVATION_SCHEDULED:
          status.scheduled_activation += 1;
          status.in_progress += 1;
          break;
        case Status.COMPLETE:
          status.complete += 1;
          break;

        case Status.CANCELED:
          status.canceled += 1;
          break;
        case Status.DISCONNECTED:
          status.disconnected += 1;
          break;
        case Status.DISCONNECTED_IN_PROGRESS:
          status.disconnected_ip += 1;
          break;
        case Status.MOVE_REQUESTED:
          status.move_request += 1;
          break;
        case Status.MOVE_REQUESTED_IN_PROGRESS:
          status.move_request_ip += 1;
          break;
        default:
          break;
      }
    };
    const activeTypeParse = (order: OrdersTable) => {
      switch (order.milestone) {
        case Status.PREPARING:
        case Status.FOC_PENDING:
        case Status.FOC_ASSIGNED:
        case Status.VENDOR_COMPLETION:
        case Status.EQUIPMENT_SHIPPED:
        case Status.ACTIVATION_PENDING:
        case Status.ACTIVATION_SCHEDULED:
        case Status.IN_PROGRESS:
        case Status.ACTIVATION_COMPLETE:
          if (!activeTypes[order.service_name]) {
            activeTypes[order.service_name] = 0;
          }
          activeTypes[order.service_name] += 1;
          break;
        default:
          break;
      }
    };

    return this.getOrders(true).pipe(
      tap((orders) => {
        if (orders === null) {
          types.all = -1;
          return {};
        }
        types.all = orders.length;
        orders.forEach((o) => {
          if (!o.type) {
            return;
          }
          statusParse(o);
          activeTypeParse(o);
        });
        return orders;
      }),
      map(() => {
        if (types.all === -1) return null;
        if (types.all === 0)
          return {
            success: true,
            types,
            status,
            active_types: activeTypes,
            pending_reasons: pendingReasons,
          };
        return {
          success: true,
          types,
          status,
          active_types: activeTypes,
          pending_reasons: pendingReasons,
        };
      }),
    );
  }
}
